import {
  Details, TextInput, Tooltip, useDetails
} from '@primer/react';

import { InfoIcon } from '@primer/octicons-react';

import {
  useCallback, useEffect, useRef, useState
} from 'react';

import format from 'date-fns/format';
import range from 'lodash/range'
import round from 'lodash/round'

import usePreferredTimeRange from '../contexts/TimeRangeContext/TimeRangeContext';

import { AssayConfig } from './AssayConfig';

import BasicDropdown from '../common/BasicDropdown/BasicDropdown';
import CardHeader from '../common/Labels/CardHeader';
import Label from '../common/Labels/Label';
import TextInputWithError from '../common/TextInputs/TextInputWithError/TextInputWithError';

import AssayCustomBinningTable from './AssayCustomBinningTable/AssayCustomBinningTable';
import ModalAssayChart from './ModalAssayChart/ModalAssayChart';
import TimeRangePicker from '../TimeRangePicker/TimeRangePicker';

import { AssayRunProvider, useAssayRunContext } from './AssayRunContext/AssayRunContext';
import { mapOnlyAssayResults } from '../../utils/assay_utils';
import useAssayLoadingContext from './AssayLoadingContext';

import './style.css'
import useClientContext from '../contexts/ClientData/ClientData';



// model-insights::assay::mod.rs:102
const SchedulingOptions = [
  { id: 'minute', label: 'Minute' },
  { id: 'hour', label: 'Hour' },
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
]

const GroupingOptions = [
  { id: 'minute', label: 'Every Minute' },
  { id: 'hour', label: 'Hourly' },
  { id: 'day', label: 'Daily' },
  { id: 'week', label: 'Weekly' },
  { id: 'month', label: 'Monthly' }
]

// Number of Bins
const AggregationBinOptions = range(5, 15, 1).map(bin => ({ id: bin, label: `${bin} Bins` }));

const AggregationTypeOptions = [
  { id: 'Density', label: 'Density' },
  { id: 'Cumulative', label: 'Cumulative' },
]

const MetricOptions = [
  { id: 'PSI', label: 'Population Stability Index' },
  { id: 'MaxDiff', label: 'Maximum Difference of Bins' },
  { id: 'SumDiff', label: 'Sum of the Difference of Bins' },
]

const BinOptions = [
  { id: 'Equal', label: 'Equally Spaced' },
  { id: 'Quantile', label: 'Quantile' },
  { id: 'Provided', label: 'Custom' }
]

const BinWeightsOptions = [
  { id: 'Equal', label: 'Equally Weighted' },
  { id: 'Provided', label: 'Custom' }
]

const ThresholdOptions = range(0.1, 0.95, 0.05).map((x) => {
  const val = round(x, 2);
  return { id: val, label: val };
});

const SchedulingTime = range(1, 24).map((h) => {
  let label = '12:00 AM';
  if (h - 12 > 0) {
    label = `${h - 12}:00 PM`;
  } else if (h === 12) {
    label = '12:00 AM'
  } else {
    label = `${h}:00 AM`;
  }

  return { id: h, label }
})

const ChartSection = ({
  assayConfig,
  setError,
}) => {
  const [selectedRun, setSelectedRun] = useState(null);

  const { assayPreviewLoading, setAssayPreviewLoading } = useAssayLoadingContext();

  const {
    assayRunData,
    assayRunError,
    assayRunLoading,
    summaryError,
    summaryLoading,
  } = useAssayRunContext();

  // Handle Errors
  useEffect(() => {
    if (assayRunError?.code === 500) {
      setError(assayRunError.msg);
    } else if (assayRunError || summaryError) {
      if (!assayRunData?.length) {
        setError('No results in selected time range.')
      } else {
        setError('Failed to load results for selected options.')
      }
    } else if (!assayRunData?.length) {
      setError('No results in selected time range.')
    } else {
      setError(null);
    }
  }, [assayRunData, assayRunError, setError, summaryError]);

  // Handle Loading States
  useEffect(() => {
    if (assayRunLoading || summaryLoading) {
      setAssayPreviewLoading(true);
    } else {
      setAssayPreviewLoading(false);
    }
  }, [assayRunLoading, summaryLoading, setAssayPreviewLoading]);

  return (
    <ModalAssayChart
      assayName="Preview"
      assayDetails={assayConfig}
      assayRunResults={assayRunData ?? []}
      setSelectedRun={setSelectedRun}
      selectedRun={selectedRun ? mapOnlyAssayResults(selectedRun) : null}
      isLoading={assayPreviewLoading}
      hideNormalDropdownOptions
      showIconMenu
      loadingText="Generating preview..."
    />
  )
}

const SimpleDateTimePicker = ({ setSchedulingDateTime, schedulingDateTime }) => (
  <input
    className="border border-border-dark rounded px-2"
    style={{ width: '220px' }}
    type="datetime-local"
    value={schedulingDateTime}
    onChange={(e) => {
      setSchedulingDateTime(format(new Date(e.target.value), "yyyy-MM-dd'T'HH:mm"))
    }}
  />
)

const AdvancedSettingsMenu = ({
  assayConfig,
  setAssayConfig,
  setError,
  startTimestamp,
  endTimestamp,
}) => {
  const [binWeightsFlag, setBinWeightsFlag] = useState(BinWeightsOptions[0].id);
  const [schedulingDateTime, setSchedulingDateTime] = useState(startTimestamp)
  const { isAssaysV2Enabled } = useClientContext();

  const [advancedSettings, setAdvancedSettings] = useState({
    run_freq_num: 1,
    run_freq: SchedulingOptions[2].id,
    grouping_freq: GroupingOptions[2].id,
    next_run_time: SchedulingTime[0].id,
    aggType: assayConfig.summarizer.aggregation ?? AggregationTypeOptions[0].id,
    numBins: assayConfig.summarizer.num_bins ?? AggregationBinOptions[0].id,
    threshold: assayConfig.alert_threshold ?? ThresholdOptions[0].id,
    metric: assayConfig.summarizer.metric ?? MetricOptions[0].id,
    binMode: assayConfig.summarizer.bin_mode ?? BinOptions[2].id,
    binWeights: assayConfig.summarizer.bin_weights ?? null,
    edges: assayConfig.summarizer.provided_edges ?? null,
  });

  const {
    numBins: numberOfBins,
    binMode,
  } = advancedSettings;
  const useCustomBinningMode = binMode === BinOptions[2].id;

  useEffect(() => {
    if (isAssaysV2Enabled) {
      setSchedulingDateTime(format(new Date(startTimestamp), "yyyy-MM-dd'T'HH:mm"))
    } else {
      /**
       * @deprecated Assays V1 Logic
       */
      setSchedulingDateTime(format(new Date(), "yyyy-MM-dd'T'HH:mm"))
    }
  }, [startTimestamp, isAssaysV2Enabled])

  useEffect(() => {
    // Clear error on change.
    setError(null);
    setAssayConfig(conf => new AssayConfig(conf.setAdvancedSettings(advancedSettings, schedulingDateTime)));
  }, [advancedSettings, endTimestamp, schedulingDateTime, setAssayConfig, setError, startTimestamp]);

  const setEdges = useCallback((edges) => {
    setAdvancedSettings(settings => ({ ...settings, edges }))
  }, []);

  const setWeights = useCallback((binWeights) => {
    setAdvancedSettings(settings => ({ ...settings, binWeights }))
  }, []);

  const schedulingChange = (value) => {
    setAdvancedSettings(settings => ({ ...settings, run_freq_num: value }))
  }

  return (
    <>
      <div className="flex items-start justify-start p-2 mx-2">
        <div className="flex flex-col items-start justify-start">
          <div className="flex">
            <Label bold>Scheduling</Label>
            <Tooltip aria-label="Configure the start time/frequency when the new analysis is run." wrap direction="ne">
              <InfoIcon size={16} className="ml-2" />
            </Tooltip>
          </div>
          <div className="flex items-center relative">
            <div className="text-md mr-1" style={{ fontWeight: '600', color: 'rgb(3, 55, 78)' }}>Every</div>
            <TextInput
              id="schedulingInterval"
              type="number"
              className="w-16"
              size="small"
              defaultValue={advancedSettings.run_freq_num}
              onChange={e => schedulingChange(e.target.value)}
            />
            <BasicDropdown
              popoverClass="h-40 overflow-scroll"
              direction="top-left"
              dropdownLabel={null}
              buttonLabelSize="md"
              iconFill="brand"
              iconSize={12}
              // We don't need to hydrate this from a config, so local state is okay.
              selectedValue={advancedSettings.run_freq}
              isDisabled={false}
              hideLabelInSmallViewports={false}
              values={SchedulingOptions}
              onValueSelected={e => setAdvancedSettings(settings => ({ ...settings, run_freq: e }))}
              hideBorder
            />
            <div className="text-md mr-1" style={{ fontWeight: '600', color: 'rgb(3, 55, 78)' }}>starting</div>
            <SimpleDateTimePicker
              setSchedulingDateTime={setSchedulingDateTime}
              schedulingDateTime={schedulingDateTime}
            />
          </div>
        </div>
        <div className="flex flex-col items-start justify-start ml-6">
          <div className="flex">
            <Label bold>Group Results</Label>
            <Tooltip aria-label="How the assay results are grouped by.">
              <InfoIcon size={16} className="ml-2" />
            </Tooltip>
          </div>
          <div className="flex items-center relative">
            <BasicDropdown
              popoverClass="h-40 overflow-scroll"
              direction="top-left"
              dropdownLabel={null}
              buttonLabelSize="md"
              iconFill="brand"
              iconSize={12}
              // We don't need to hydrate this from a config, so local state is okay.
              selectedValue={advancedSettings.grouping_freq}
              isDisabled={false}
              hideLabelInSmallViewports={false}
              values={GroupingOptions}
              onValueSelected={e => setAdvancedSettings(settings => ({ ...settings, grouping_freq: e }))}
              hideBorder
            />
          </div>
        </div>
      </div>
      <div className="flex items-start justify-between relative p-2 mx-2">
        <div className="flex flex-col items-start justify-center w-1/3">
          <div className="flex">
            <Label bold>Aggregation Type</Label>
            <Tooltip aria-label="The method for collecting the bin values and percentages." wrap direction="ne">
              <InfoIcon size={16} className="ml-2" />
            </Tooltip>
          </div>
          <BasicDropdown
            popoverClass="h-40 overflow-scroll"
            direction="top-left"
            dropdownLabel={null}
            buttonLabelSize="md"
            iconFill="brand"
            iconSize={12}
            selectedValue={advancedSettings.aggType}
            isDisabled={false}
            hideLabelInSmallViewports={false}
            values={AggregationTypeOptions}
            onValueSelected={e => setAdvancedSettings(settings => ({ ...settings, aggType: e }))}
            hideBorder
          />
        </div>
        <div className="flex flex-col items-start justify-center w-1/3">
          <div className="flex">
            <Label bold>Threshold</Label>
            <Tooltip aria-label="Threshold for deciding the difference between distributions based on the Metric type." wrap>
              <InfoIcon size={16} className="ml-2" />
            </Tooltip>
          </div>
          <TextInputWithError
            type="number"
            step={0.06}
            // Fixes number change on scrolling up or down on input
            onWheel={e => e.target.blur()}
            errorMessage="Please enter a numeric value."
            onChange={e => setAdvancedSettings(settings => ({ ...settings, threshold: e.toString() }))}
            value={advancedSettings.threshold}
            wrapperStyle={{ marginTop: '8px' }}
          />
        </div>
        <div className="flex flex-col items-start justify-center w-1/3">
          <div className="flex">
            <Label bold>Metric</Label>
            <Tooltip aria-label="Method used to score the bins.">
              <InfoIcon size={16} className="ml-2" />
            </Tooltip>
          </div>
          <BasicDropdown
            popoverClass="h-40 overflow-scroll"
            direction="top-left"
            dropdownLabel={null}
            buttonLabelSize="md"
            iconFill="brand"
            iconSize={12}
            selectedValue={advancedSettings.metric}
            isDisabled={false}
            hideLabelInSmallViewports={false}
            values={MetricOptions}
            onValueSelected={e => setAdvancedSettings(settings => ({ ...settings, metric: e }))}
            hideBorder
          />
        </div>
      </div>
      <div className="flex items-start justify-between relative p-2 mx-2">
        <div className="flex flex-col items-start justify-center w-1/3">
          <div className="flex">
            <Label bold>Number of Bins</Label>
            <Tooltip aria-label="Number of bins used to partition the analysis data.">
              <InfoIcon size={16} className="ml-2" />
            </Tooltip>
          </div>
          <BasicDropdown
            popoverClass="h-40 overflow-scroll"
            direction="top-left"
            dropdownLabel={null}
            buttonLabelSize="md"
            iconFill="brand"
            iconSize={12}
            selectedValue={advancedSettings.numBins}
            isDisabled={false}
            hideLabelInSmallViewports={false}
            values={AggregationBinOptions}
            onValueSelected={e => setAdvancedSettings(settings => ({ ...settings, numBins: e }))}
            hideBorder
          />
        </div>
        <div className="flex flex-col items-start justify-center w-1/3">
          <div className="flex">
            <Label bold>Bin Mode</Label>
            <Tooltip aria-label="Set the binning scheme.">
              <InfoIcon size={16} className="ml-2" />
            </Tooltip>
          </div>
          <BasicDropdown
            popoverClass="h-40 overflow-scroll"
            direction="top-left"
            dropdownLabel={null}
            buttonLabelSize="md"
            iconFill="brand"
            iconSize={12}
            selectedValue={advancedSettings.binMode}
            isDisabled={false}
            hideLabelInSmallViewports={false}
            values={BinOptions}
            onValueSelected={e => setAdvancedSettings(settings => ({ ...settings, binMode: e }))}
            hideBorder
          />
        </div>
        <div className="flex flex-col items-start justify-center w-1/3">
          <div className="flex">
            <Label bold>Bin Weights</Label>
            <Tooltip aria-label="The weight applied to each bin.">
              <InfoIcon size={16} className="ml-2" />
            </Tooltip>
          </div>
          <BasicDropdown
            popoverClass="h-40 overflow-scroll"
            direction="top-left"
            dropdownLabel={null}
            buttonLabelSize="md"
            iconFill="brand"
            iconSize={12}
            selectedValue={binWeightsFlag}
            isDisabled={false}
            hideLabelInSmallViewports={false}
            values={BinWeightsOptions}
            onValueSelected={e => setBinWeightsFlag(e)}
            hideBorder
          />
        </div>
      </div>
      <div className="flex items-start relative p-2 mx-2">
        <AssayCustomBinningTable
          numberOfBins={numberOfBins}
          setEdges={setEdges}
          setWeights={setWeights}
          useCustomBinEdges={useCustomBinningMode}
          useCustomWeights={binWeightsFlag === BinWeightsOptions[1].id}
        />
      </div>
    </>
  )
}

export const PreviewHistorical = ({
  assayConfig,
  setAssayConfig,
  setBaselineSummary,
  setError,
}) => {
  const ref = useRef(null);
  const {
    getDetailsProps, open, onToggle
  } = useDetails({ closeOnOutsideClick: false, ref })

  const {
    startTimestamp,
    endTimestamp,
    selectedTimeRange,
    onTimeRangeChange,
  } = usePreferredTimeRange()

  return <div className="flex flex-col h-full">
    <div className="flex flex-col items-start mb-2">
      <CardHeader>Preview Historical Data</CardHeader>
      <Label className="my-2" size="sm">
        Preview Data based on selected timeline.
      </Label>
      <TimeRangePicker selectedRange={selectedTimeRange} defaultRange={selectedTimeRange} onChange={onTimeRangeChange} />
    </div>
    <AssayRunProvider
      assayConfig={assayConfig}
      setBaselineSummary={setBaselineSummary}
      startTimestamp={startTimestamp}
      endTimestamp={endTimestamp}
    >
      <ChartSection
        assayConfig={assayConfig}
        setError={setError}
      />
    </AssayRunProvider>
    <Details
      {...getDetailsProps()}
      onToggle={(...x) => {
        if (x === ref) {
          onToggle();
        }
      }}
    >
      <summary className="flex cursor-pointer" style={{ padding: '10px 0px' }}><b>Advanced Settings {open ? <>&#9650;</> : <>&#9660;</>}</b></summary>
      <AdvancedSettingsMenu
        startTimestamp={assayConfig?.baseline?.calculated?.fixed_window?.start ?? startTimestamp}
        endTimestamp={endTimestamp}
        assayConfig={assayConfig}
        setAssayConfig={setAssayConfig}
        setError={setError}
      />
    </Details>
  </div>;
};

export default PreviewHistorical;
